import { trafficPartPrefix, trafficMatchingPrefix } from '../trafficQuestionNumbering';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { doc, FieldPath, onSnapshot, updateDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import { db } from '../firebase-config';
import { getUrlParams } from '../common';
import { sanitizeTrafficCourseTasks } from '../trafficQuestionSchema';
import { clearReturnedTrafficMarks } from '../trafficCourseFlowUtils';
import Logo from '../assets/Logo.svg';
import './TrafficQuestionLayout.css';
import {
  buildTrafficTaskStateMap,
  buildNextTrafficTaskStates,
  getCurrentAvailableTaskId,
  getFlowTaskById,
  getOrderedStudentTasks,
  getTaskState,
  getTrafficStageStatusLabel,
  getTrafficDeclarationPath,
  isTaskSubmitted,
  isTrafficTaskAccessible,
  STUDENT_DECLARATION_TYPE,
  theoryNeedsTrainerMarking,
} from '../trafficCourseFlowUtils';
import { clearPageMessage, showLoadingMessage, showWarningMessage } from '../successMessage';

export { sanitizeTrafficCourseTasks } from '../trafficQuestionSchema';

const stageCardStyle = {
  border: '1px solid #e5e7eb',
  borderRadius: '14px',
  padding: '18px',
  background: '#fff',
  boxShadow: '0 10px 30px rgba(15, 23, 42, 0.05)',
};

const contentBlockStyle = {
  background: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: '12px',
  padding: '18px',
  marginBottom: '18px',
};

const choiceResponseTypes = ['multiple-choice', 'true-false', 'yes-no', 'image-multiple-choice', 'multiple-response'];
const writtenResponseTypes = ['short-answer', 'long-answer', 'diagram-questions', 'fill-blank', 'name-following', 'numeric', 'describe-image'];
// A word bank can support matching as well as fill-in-the-blank questions. Only
// use text inputs when the question is explicitly a blank, or has blank parts.
const isWordBankFillBlank = (question = {}) => question.type === 'fill-blank'
  || (Array.isArray(question.subQuestions) && question.subQuestions.length > 0 && /word\s*bank/i.test(String(question.text || '')));

const buildHydratedTaskResponse = (task = {}, flowDoc = {}) => {
  const initialResponse = getInitialTaskResponse(task, flowDoc.responses?.[task.id] || {});
  return task.type === STUDENT_DECLARATION_TYPE
    ? {
      ...initialResponse,
      studentName: `${flowDoc.firstName || ''} ${flowDoc.lastName || ''}`.trim(),
      declarationDate: initialResponse.declarationDate || new Date().toISOString().slice(0, 10),
    }
    : initialResponse;
};

const getInitialTaskResponse = (task = {}, existingResponse = {}) => {
  if (existingResponse && Object.keys(existingResponse).length > 0) {
    return existingResponse;
  }

  if (task.type === STUDENT_DECLARATION_TYPE) {
    return {
      studentName: '',
      declarationDate: new Date().toISOString().slice(0, 10),
      signature: '',
    };
  }

  if (task.type === 'Theory Questions') {
    return {
      answers: {},
    };
  }

  return {
    acknowledged: false,
    notes: '',
  };
};

const normalizeAnswerText = (value) => String(value ?? '').trim().toLowerCase().replace(/\s+/g, ' ');

const matchesAcceptedText = (value, expected) => {
  const acceptedAnswers = [String(expected ?? ''), ...String(expected ?? '')
    .split(/\s*\/\s*|\s*;\s*|\s*,\s*|\r?\n/)]
    .map(normalizeAnswerText)
    .filter(Boolean);
  return acceptedAnswers.length > 0 && acceptedAnswers.includes(normalizeAnswerText(value));
};

// Some existing diagram questions were configured as short-answer fields but
// already contain a complete option pool and answer key. Treat those as choices
// so students do not need to reproduce an exact phrase in a text box.
const getOptionBackedCorrectIndexes = (question = {}) => {
  const options = (question.options || []).map((option) => String(option || '').trim());
  if (options.length < 2 || options.some((option) => !option)) return [];

  const savedChoiceIndex = String(question.correctAnswer ?? '').trim();
  if (/^\d+$/.test(savedChoiceIndex) && Number(savedChoiceIndex) < options.length) {
    return [Number(savedChoiceIndex)];
  }

  const configuredAnswers = [question.acceptedAnswer, question.answerGuide, question.correctAnswer];
  const indexes = configuredAnswers.flatMap((answer) => {
    return options.reduce((matches, option, index) => (
      matchesAcceptedText(option, answer) ? [...matches, index] : matches
    ), []);
  });
  return [...new Set(indexes)];
};

const isOptionBackedTextResponse = (question = {}) => (
  question.responseType === 'short-answer' && getOptionBackedCorrectIndexes(question).length === 1
);

export const QuestionRenderer = ({ question, value, onChange }) => {
  const updateNestedValue = (partId, nestedValue) => onChange({ ...(value || {}), [partId]: nestedValue });
  const wordBankFillBlank = isWordBankFillBlank(question);
  const wordBankOptions = Array.isArray(question.wordBankOptions)
    ? question.wordBankOptions.filter((option) => String(option || '').trim())
    : [];
  const displayAsFillBlank = question.type === 'fill-blank' || wordBankFillBlank;
  const imageSelection = question.type === 'image-multiple-choice' && String(question.numberLabel) === '108' && question.mediaUrl;
  const sideBySide = /^(3|33|54[a-b]?|96)$/.test(String(question.numberLabel));
  const renderOptionList = (options = [], selected, inputType = 'radio', allowMultiple = false, handleOptionChange = onChange) => (
    <div className={imageSelection ? 'trafficImageSelections' : undefined} style={{ display: 'grid', gap: '10px', marginTop: '10px' }}>
      {options.map((option, optionIndex) => {
        const checked = allowMultiple ? Array.isArray(selected) && selected.includes(String(optionIndex)) : String(selected || '') === String(optionIndex);
        return (
          <label key={`${question.id}-${optionIndex}`} style={{ display: 'flex', alignItems: 'center', gap: '10px', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '10px 12px' }}>
            <input
              type={inputType}
              checked={checked}
              onChange={() => {
                if (allowMultiple) {
                  const nextSelected = Array.isArray(selected) ? [...selected] : [];
                  if (nextSelected.includes(String(optionIndex))) {
                    handleOptionChange(nextSelected.filter((entry) => entry !== String(optionIndex)));
                  } else {
                    handleOptionChange([...nextSelected, String(optionIndex)]);
                  }
                  return;
                }
                handleOptionChange(String(optionIndex));
              }}
            />
            <span>{option || `Option ${optionIndex + 1}`}</span>
          </label>
        );
      })}
    </div>
  );

  if (question.type === 'student-guide') {
    return (
      <div data-question-id={question.id} style={{ ...contentBlockStyle, background: '#f8fafc' }}>
        <strong style={{ display: 'block', marginBottom: '8px' }}>Guide / Reference</strong>
        <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{question.text}</p>
      </div>
    );
  }

  if (question.subQuestions?.length) {
    return (
      <div data-question-id={question.id} style={contentBlockStyle}>
        <p style={{ marginTop: 0, fontWeight: 700 }}>{question.numberLabel}. {question.text}</p>
        {question.mediaUrl && <img src={question.mediaUrl} alt="" style={{ maxWidth: '100%', marginBottom: '14px' }} />}
        {wordBankOptions.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }} aria-label="Word bank">
            <strong style={{ width: '100%' }}>Word bank</strong>
            {wordBankOptions.map((option) => <span key={option} style={{ border: '1px solid #cbd5e1', borderRadius: '999px', padding: '6px 12px', background: '#f8fafc' }}>{option}</span>)}
          </div>
        )}
        <div style={{ display: 'grid', gap: '16px' }}>
          {question.subQuestions.map((part, index) => {
            const partValue = value?.[part.id];
            const responseType = part.responseType || question.type;
            const optionBackedTextResponse = isOptionBackedTextResponse(part);
            return (
              <div key={part.id} data-part-id={part.id} style={{ border: '1px solid #edf2f7', borderRadius: '10px', padding: '14px' }}>
                <strong style={{ display: 'block', marginBottom: '8px' }}>{trafficPartPrefix(question, index)}{part.text}</strong>
                <div className={part.mediaUrl && sideBySide ? 'trafficQuestionMediaLayout' : undefined}>
                {part.mediaUrl && <img src={part.mediaUrl} alt="" style={{ maxWidth: '100%', marginBottom: '12px' }} />}
                <div>
                {wordBankFillBlank && (
                  wordBankOptions.length > 0 ? (
                    <select
                      value={partValue || ''}
                      onChange={(event) => updateNestedValue(part.id, event.target.value)}
                      aria-label={`Answer for ${String.fromCharCode(97 + index)}`}
                      style={{ width: '220px', maxWidth: '100%', minHeight: '42px', border: '1px solid #cbd5e1', borderRadius: '10px', padding: '10px 12px', boxSizing: 'border-box', background: '#fff' }}
                    >
                      <option value="">Select the correct step</option>
                      {wordBankOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                    </select>
                  ) : (
                    <input
                      type='text'
                      value={partValue || ''}
                      onChange={(event) => updateNestedValue(part.id, event.target.value)}
                      aria-label={`Answer for ${String.fromCharCode(97 + index)}`}
                      style={{ width: '220px', maxWidth: '100%', minHeight: '42px', border: '1px solid #cbd5e1', borderRadius: '10px', padding: '10px 12px', boxSizing: 'border-box' }}
                    />
                  )
                )}
                {!wordBankFillBlank && (choiceResponseTypes.includes(responseType) || optionBackedTextResponse) && renderOptionList(
                  part.options || [],
                  partValue,
                  responseType === 'multiple-response' || part.allowMultiple ? 'checkbox' : 'radio',
                  responseType === 'multiple-response' || part.allowMultiple,
                  (nextValue) => updateNestedValue(part.id, nextValue),
                )}
                {!wordBankFillBlank && responseType === 'verbal' && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input type='checkbox' checked={Boolean(partValue)} onChange={(event) => updateNestedValue(part.id, event.target.checked)} />
                    <span>I completed this verbal response with my trainer.</span>
                  </label>
                )}
                {!wordBankFillBlank && !optionBackedTextResponse && writtenResponseTypes.includes(responseType) && (
                  <textarea
                    value={partValue || ''}
                    onChange={(event) => updateNestedValue(part.id, event.target.value)}
                    rows={1}
                    style={{ width: '100%', minHeight: '42px', height: '42px', border: '1px solid #cbd5e1', borderRadius: '10px', padding: '10px 12px', boxSizing: 'border-box', resize: 'vertical' }}
                  />
                )}
                </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div data-question-id={question.id} style={contentBlockStyle}>
      {displayAsFillBlank ? (
        <div style={{ margin: '0 0 12px', fontWeight: 700, lineHeight: 1.8 }}>
          <span>{question.numberLabel}. </span>
          {String(question.text || '').includes('___') ? (
            String(question.text || '').split('___').map((textPart, index, parts) => (
              <React.Fragment key={`${question.id}-blank-${index}`}>
                <span>{textPart}</span>
                {index < parts.length - 1 && (
                  <input
                    type='text'
                    value={value || ''}
                    onChange={(event) => onChange(event.target.value)}
                    aria-label={`Answer for question ${question.numberLabel}`}
                    style={{ width: '190px', maxWidth: '100%', margin: '0 7px', border: '0', borderBottom: '2px solid #64748b', background: '#f8fafc', padding: '5px 8px', boxSizing: 'border-box', font: 'inherit', outline: 'none' }}
                  />
                )}
              </React.Fragment>
            ))
          ) : (
            <><span>{question.text} </span><input type='text' value={value || ''} onChange={(event) => onChange(event.target.value)} aria-label={`Answer for question ${question.numberLabel}`} style={{ width: '190px', maxWidth: '100%', marginLeft: '7px', border: '0', borderBottom: '2px solid #64748b', background: '#f8fafc', padding: '5px 8px', boxSizing: 'border-box', font: 'inherit', outline: 'none' }} /></>
          )}
        </div>
      ) : (
        <p style={{ marginTop: 0, fontWeight: 700 }}>{question.numberLabel}. {question.text}</p>
      )}
      <div className={question.mediaUrl && sideBySide ? 'trafficQuestionMediaLayout' : undefined}>
      {question.mediaUrl && <img src={question.mediaUrl} alt="" style={{ maxWidth: '100%', marginBottom: '14px' }} />}
      <div>
      {!displayAsFillBlank && choiceResponseTypes.includes(question.type) && renderOptionList(
        question.options || [],
        value,
        question.type === 'multiple-response' || question.allowMultiple ? 'checkbox' : 'radio',
        question.type === 'multiple-response' || question.allowMultiple,
      )}
      {question.type === 'matching' && (
        <div style={{ display: 'grid', gap: '12px' }}>
          {(() => {
            // Keep a stable neutral order instead of exposing the answer-key sequence.
            const answers = [...new Set((question.matchingPairs || []).map((pair) => pair.right).filter(Boolean))].sort((a, b) => a.localeCompare(b));
            return (question.matchingPairs || []).map((pair, index) => (
              <label key={pair.id} style={{ display: 'grid', gap: '6px' }}>
                <span>{trafficMatchingPrefix(question, index)}{pair.left}</span>
                <select
                  value={value?.[pair.id] || ''}
                  onChange={(event) => onChange({ ...(value || {}), [pair.id]: event.target.value })}
                  style={{ border: '1px solid #cbd5e1', borderRadius: '10px', padding: '10px', background: '#fff' }}
                >
                  <option value="">Select a match</option>
                  {answers.map((answer, index) => <option key={`${pair.id}-${index}`} value={answer}>{answer}</option>)}
                </select>
              </label>
            ));
          })()}
        </div>
      )}
      {question.type === 'ordering' && (
        <div style={{ display: 'grid', gap: '12px' }}>
          {question.orderingInputMode === 'drag' && (
            <p style={{ margin: 0, color: '#475569', fontSize: '14px' }}>
              Drag every item into the correct order. The starting order is mixed.
            </p>
          )}
          {(question.orderingInputMode === 'drag'
            ? [...(question.orderingItems || [])].sort((left, right) => {
              const leftPosition = Number(value?.[left.id] || (question.orderingItems || []).length - (question.orderingItems || []).findIndex((item) => item.id === left.id));
              const rightPosition = Number(value?.[right.id] || (question.orderingItems || []).length - (question.orderingItems || []).findIndex((item) => item.id === right.id));
              return leftPosition - rightPosition;
            })
            : (question.orderingItems || [])).map((item, index, displayedItems) => (
            <label key={item.id} style={{ display: 'grid', gap: '6px' }}>
              {question.orderingInputMode === 'drag' ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', border: '1px solid #cbd5e1', borderRadius: '10px', padding: '12px', background: '#fff' }}>
                  <div
                    draggable
                    onDragStart={(event) => event.dataTransfer.setData('text/plain', item.id)}
                    onDragOver={(event) => event.preventDefault()}
                    onDrop={(event) => {
                      event.preventDefault();
                      const draggedId = event.dataTransfer.getData('text/plain');
                      if (!draggedId || draggedId === item.id) return;
                      const nextItems = [...displayedItems];
                      const fromIndex = nextItems.findIndex((entry) => entry.id === draggedId);
                      const toIndex = nextItems.findIndex((entry) => entry.id === item.id);
                      const [dragged] = nextItems.splice(fromIndex, 1);
                      nextItems.splice(toIndex, 0, dragged);
                      onChange(Object.fromEntries(nextItems.map((entry, position) => [entry.id, String(position + 1)])));
                    }}
                    style={{ flex: 1, cursor: 'grab' }}
                  >
                    <strong style={{ marginRight: '10px' }}>{index + 1}.</strong>{item.text}
                  </div>
                  <button type="button" aria-label={`Move ${item.text} up`} disabled={index === 0} onClick={() => {
                    const nextItems = [...displayedItems];
                    [nextItems[index - 1], nextItems[index]] = [nextItems[index], nextItems[index - 1]];
                    onChange(Object.fromEntries(nextItems.map((entry, position) => [entry.id, String(position + 1)])));
                  }}>Up</button>
                  <button type="button" aria-label={`Move ${item.text} down`} disabled={index === displayedItems.length - 1} onClick={() => {
                    const nextItems = [...displayedItems];
                    [nextItems[index], nextItems[index + 1]] = [nextItems[index + 1], nextItems[index]];
                    onChange(Object.fromEntries(nextItems.map((entry, position) => [entry.id, String(position + 1)])));
                  }}>Down</button>
                </div>
              ) : <><span>{item.text}</span><input type='number' min='1' max={(question.orderingItems || []).length} value={value?.[item.id] || ''} onChange={(event) => onChange({ ...(value || {}), [item.id]: event.target.value })} style={{ border: '1px solid #cbd5e1', borderRadius: '10px', padding: '10px' }} /></>}
            </label>
          ))}
        </div>
      )}
      {!displayAsFillBlank && question.type === 'verbal' && (
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <input type='checkbox' checked={Boolean(value)} onChange={(event) => onChange(event.target.checked)} />
          <span>I completed this verbal response with my trainer.</span>
        </label>
      )}
      {!displayAsFillBlank && writtenResponseTypes.includes(question.type) && (
        question.type === 'numeric' ? (
          <input
            type='number'
            value={value || ''}
            onChange={(event) => onChange(event.target.value)}
            style={{ width: '100%', minHeight: '42px', border: '1px solid #cbd5e1', borderRadius: '10px', padding: '10px 12px', boxSizing: 'border-box' }}
          />
        ) : (
          <textarea
            value={value || ''}
            onChange={(event) => onChange(event.target.value)}
            rows={['long-answer', 'describe-image'].includes(question.type) ? 5 : 2}
            style={{ width: '100%', minHeight: '42px', border: '1px solid #cbd5e1', borderRadius: '10px', padding: '10px 12px', boxSizing: 'border-box', resize: 'vertical' }}
          />
        )
      )}
      </div>
      </div>
    </div>
  );
};

const isFilledValue = (value) => {
  if (typeof value === 'boolean') return value;
  if (Array.isArray(value)) return value.length > 0;
  if (value && typeof value === 'object') {
    const entries = Object.values(value);
    return entries.length > 0 && entries.every((entry) => isFilledValue(entry));
  }
  return String(value || '').trim() !== '';
};

const arraysMatch = (value, expected) => {
  const actualValues = (Array.isArray(value) ? value : [value]).map(String).sort();
  const expectedValues = (Array.isArray(expected) ? expected : [expected]).map(String).sort();
  return actualValues.length === expectedValues.length && actualValues.every((entry, index) => entry === expectedValues[index]);
};

export const evaluateAutomaticAnswer = (question, value, forceFillBlank = false) => {
  const type = question.responseType || question.type;
  const wordBankFillBlank = isWordBankFillBlank(question);
  const displayAsFillBlank = forceFillBlank || type === 'fill-blank' || wordBankFillBlank;

  if (question.markingMode === 'trainer') {
    return { autoMarked: false, correct: true };
  }

  if (question.subQuestions?.length) {
    const results = question.subQuestions.map((part) => (
      // A fill-blank parent may store parts with a generic responseType from the
      // builder. Preserve the parent's input mode so text entries are not compared
      // to a multiple-choice option index.
      evaluateAutomaticAnswer(
        { ...part, type: part.responseType || question.type },
        value?.[part.id],
        forceFillBlank || question.type === 'fill-blank' || wordBankFillBlank,
      )
    ));
    const autoMarkedResults = results.filter((result) => result.autoMarked);
    return {
      autoMarked: autoMarkedResults.length > 0,
      correct: autoMarkedResults.every((result) => result.correct),
    };
  }
  if (isOptionBackedTextResponse(question)) {
    const correctIndexes = getOptionBackedCorrectIndexes(question);
    const selectedIndex = String(value ?? '').trim();
    // Preserve compatibility with answers entered before the option pool was
    // rendered, while newly rendered responses store the selected index.
    const correct = /^\d+$/.test(selectedIndex)
      ? correctIndexes.includes(Number(selectedIndex))
      : correctIndexes.some((index) => matchesAcceptedText(value, question.options?.[index]));
    return { autoMarked: true, correct };
  }
  if (displayAsFillBlank) {
    const configuredAnswer = Array.isArray(question.correctAnswer) ? question.correctAnswer[0] : question.correctAnswer;
    const optionIndex = Number(configuredAnswer);
    const configuredOptionAnswer = ['multiple-choice', 'image-multiple-choice', 'true-false', 'yes-no'].includes(type) && Number.isInteger(optionIndex)
      ? question.options?.[optionIndex]
      : '';
    const expectedAnswer = configuredOptionAnswer || question.acceptedAnswer || configuredAnswer || question.answerGuide;
    return { autoMarked: true, correct: matchesAcceptedText(value, expectedAnswer) };
  }
  if (type === 'multiple-response' || question.allowMultiple) {
    return { autoMarked: true, correct: arraysMatch(value, question.correctAnswer || []) };
  }
  if (['multiple-choice', 'image-multiple-choice', 'true-false', 'yes-no'].includes(type)) {
    return { autoMarked: true, correct: String(value ?? '') === String(question.correctAnswer ?? '') };
  }
  if (type === 'numeric') {
    const actualNumber = Number(value);
    const configuredAnswer = question.correctAnswer || question.acceptedAnswer || question.answerGuide;
    const expectedNumber = Number(configuredAnswer);
    return {
      autoMarked: true,
      correct: String(configuredAnswer ?? '').trim() !== '' && Number.isFinite(actualNumber) && Number.isFinite(expectedNumber) && Math.abs(actualNumber - expectedNumber) <= Number(question.tolerance || 0),
    };
  }
  if (type === 'matching') {
    const pairs = question.matchingPairs || [];
    return {
      autoMarked: true,
      correct: pairs.length > 0 && pairs.every((pair) => String(pair.left || '').trim() && matchesAcceptedText(value?.[pair.id], pair.right)),
    };
  }
  if (type === 'ordering') {
    const items = question.orderingItems || [];
    return {
      autoMarked: true,
      correct: items.length > 0 && items.every((item, index) => String(item.text || '').trim() && String(value?.[item.id] ?? '') === String(item.correctPosition ?? index + 1)),
    };
  }

  if (type === 'name-following') {
    return { autoMarked: true, correct: matchesAcceptedText(value, question.acceptedAnswer || question.correctAnswer || question.answerGuide) };
  }

  if ((question.acceptedAnswer || question.correctAnswer || question.answerGuide) && ['short-answer', 'diagram-questions'].includes(type)) {
    return { autoMarked: true, correct: matchesAcceptedText(value, question.acceptedAnswer || question.correctAnswer || question.answerGuide) };
  }

  return { autoMarked: false, correct: true };
};

export const markTheoryResponse = (task, response) => {
  const questions = (task.questions || []).filter((question) => question.type !== 'student-guide');
  const questionMarks = questions.reduce((marks, question) => {
    const result = evaluateAutomaticAnswer(question, response?.answers?.[question.id]);
    if (result.autoMarked) {
      marks[question.id] = result.correct ? 'C' : 'NYC';
    }
    return marks;
  }, {});
  return {
    questionMarks,
    incorrectQuestionIds: questions.filter((question) => questionMarks[question.id] === 'NYC').map((question) => question.id),
  };
};

const isTaskResponseComplete = (task, response, signatureReady) => {
  if (task.type === 'Student instructions' || task.type === 'Conditions of assessment' || task.type === 'Activity') {
    return true;
  }

  if (task.type === STUDENT_DECLARATION_TYPE) {
    return Boolean(response?.studentName?.trim()) && Boolean(response?.declarationDate) && signatureReady;
  }

  if (task.type === 'Theory Questions') {
    const questions = (task.questions || []).filter((question) => question.type !== 'student-guide');
    return questions.every((question) => isFilledValue(response?.answers?.[question.id]));
  }

  return true;
};

const mergeTaskStatesWithLatestTasks = (latestTasks = [], existingTaskStates = {}) => {
  const theoryReleased = latestTasks.some(task => task.type === 'Theory Questions'
    && !existingTaskStates[task.id]?.priorCompletion
    && ['available', 'submitted', 'completed'].includes(existingTaskStates[task.id]?.status));
  const baseStates = buildTrafficTaskStateMap(latestTasks);
  return latestTasks.reduce((acc, task) => {
    const existingState = existingTaskStates?.[task.id];
    const mergedState = existingState
      ? {
        ...baseStates[task.id],
        ...existingState,
        taskId: task.id,
        type: task.type || '',
        name: task.name || '',
        studentVisible: baseStates[task.id]?.studentVisible,
      }
      : baseStates[task.id];
    if (['Student instructions', 'Conditions of assessment'].includes(task.type)
      && !['submitted', 'completed'].includes(mergedState?.status)) {
      mergedState.status = theoryReleased ? 'available' : 'locked';
    }
    acc[task.id] = mergedState;
    return acc;
  }, {});
};

// Automatic results belong to the exact question template that produced them.
// Preserve entered answers across a builder update, but recalculate assessment
// results instead of carrying an old retry list into changed questions.
export const invalidateTheoryAutoMarksForCourseUpdate = (responses = {}, tasks = []) => {
  const taskById = new Map((tasks || []).map((task) => [String(task.id), task]));
  return Object.entries(responses || {}).reduce((acc, [taskId, response]) => {
    const task = taskById.get(String(taskId));
    if (task?.type !== 'Theory Questions' || !response?.autoMark) {
      acc[taskId] = response;
      return acc;
    }
    const { autoMark, ...responseWithoutAutoMark } = response;
    acc[taskId] = responseWithoutAutoMark;
    return acc;
  }, {});
};

const TrafficCourseFlow = () => {
  const navigate = useNavigate();
  const params = getUrlParams();
  const sid = params.sid || '';
  const [flowDoc, setFlowDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [activeTaskId, setActiveTaskId] = useState('');
  const [draftResponse, setDraftResponse] = useState({});
  const [completionMessage, setCompletionMessage] = useState('');
  const [theoryPage, setTheoryPage] = useState(0);
  const [autosaveStatus, setAutosaveStatus] = useState('');
  const [savingTheoryPage, setSavingTheoryPage] = useState(false);
  const [showTheoryRetryIntro, setShowTheoryRetryIntro] = useState(false);
  const signatureRef = useRef(null);
  const hydratedTaskIdRef = useRef('');
  const [hydratedTaskId, setHydratedTaskId] = useState('');
  const hydratedResponseRef = useRef('');
  const localDraftDirtyRef = useRef(false);
  const [refreshAttempt, setRefreshAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let unsubscribe = () => {};
    const controller = new AbortController();
    setLoading(true);
    setFlowDoc(null);
    const load = async () => {
      try {
        if (!sid) throw new Error('This traffic course session is invalid.');
        const response = await fetch('https://australia-southeast1-upskillinstitute-au.cloudfunctions.net/syncTrafficCourseFlow', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ flowId: sid }), signal: controller.signal,
        });
        if (!response.ok) throw new Error('Unable to refresh your assessment. Please try again or contact your trainer.');
        if (cancelled) return;
        unsubscribe = onSnapshot(doc(db, 'trafficCourseFlows', sid), snapshot => {
          if (cancelled) return;
          if (!snapshot.exists()) {
            setError('This traffic course session could not be found.');
            setFlowDoc(null);
          } else {
            const data = snapshot.data();
            const tasks = sanitizeTrafficCourseTasks(data.tasks || []);
            setFlowDoc({ ...data, id: snapshot.id, tasks,
              taskStates: mergeTaskStatesWithLatestTasks(tasks, data.taskStates || {}) });
            setError('');
          }
          setLoading(false);
        }, () => {
          if (!cancelled) {
            setFlowDoc(null);
            setError('Unable to load your assessment. Please try again.');
            setLoading(false);
          }
        });
      } catch (error) {
        if (!cancelled) {
          setError(error.message || 'Unable to refresh your assessment. Please try again.');
          setLoading(false);
        }
      }
    };
    load();
    return () => { cancelled = true; controller.abort(); unsubscribe(); };
  }, [sid, refreshAttempt]);

  const visibleTasks = useMemo(() => getOrderedStudentTasks(flowDoc || {}), [flowDoc]);
  const activeTask = useMemo(() => getFlowTaskById(flowDoc || {}, activeTaskId) || getFlowTaskById(flowDoc || {}, getCurrentAvailableTaskId(flowDoc || {})), [flowDoc, activeTaskId]);
  const activeTaskState = useMemo(() => (activeTask ? getTaskState(flowDoc || {}, activeTask.id) : null), [flowDoc, activeTask]);
  const retryTheoryQuestionIds = useMemo(
    () => [...new Set([...(draftResponse.autoMark?.incorrectQuestionIds || []), ...(draftResponse.assessorRetryQuestionIds || [])])],
    [draftResponse.autoMark?.incorrectQuestionIds, draftResponse.assessorRetryQuestionIds],
  );
  const retryTheoryQuestions = useMemo(() => {
    const retryQuestionIdSet = new Set(retryTheoryQuestionIds);
    return retryQuestionIdSet.size > 0
      ? (activeTask?.questions || []).filter((question) => question.type !== 'student-guide' && retryQuestionIdSet.has(question.id))
      : [];
  }, [activeTask?.questions, retryTheoryQuestionIds]);
  const remainingRetryTheoryQuestionIds = useMemo(() => (
    retryTheoryQuestions
      .filter((question) => {
        const result = evaluateAutomaticAnswer(question, draftResponse.answers?.[question.id]);
        return result.autoMarked && !result.correct;
      })
      .map((question) => question.id)
  ), [draftResponse.answers, retryTheoryQuestions]);
  const theoryQuestionPages = useMemo(() => {
    const pages = [[]];
    let numberedQuestionCount = 0;
    const retryQuestionIdSet = new Set(retryTheoryQuestionIds);
    const pageQuestions = retryQuestionIdSet.size > 0
      ? (activeTask?.questions || []).filter((question, index, allQuestions) => {
        if (question.type !== 'student-guide') return retryQuestionIdSet.has(question.id);
        // Keep a guide with the question it introduces during a targeted retry.
        return retryQuestionIdSet.has(allQuestions.slice(index + 1).find((item) => item.type !== 'student-guide')?.id);
      })
      : (activeTask?.questions || []);

    pageQuestions.forEach((question) => {
      const isNumberedQuestion = question.type !== 'student-guide';
      if (isNumberedQuestion && numberedQuestionCount === 3) {
        pages.push([]);
        numberedQuestionCount = 0;
      }

      pages[pages.length - 1].push(question);
      if (isNumberedQuestion) {
        numberedQuestionCount += 1;
      }
    });

    return pages;
  }, [activeTask, retryTheoryQuestionIds]);
  const currentTheoryPageComplete = useMemo(() => (
    (theoryQuestionPages[theoryPage] || [])
      .filter((question) => question.type !== 'student-guide')
      .every((question) => isFilledValue(draftResponse.answers?.[question.id]))
  ), [draftResponse.answers, theoryPage, theoryQuestionPages]);

  useEffect(() => {
    if (!flowDoc) return;
    const selected = getFlowTaskById(flowDoc, activeTaskId);
    const nextTaskId = selected && isTrafficTaskAccessible(flowDoc, selected) ? activeTaskId : getCurrentAvailableTaskId(flowDoc);
    setActiveTaskId(nextTaskId);
  }, [flowDoc, activeTaskId]);

  useEffect(() => {
    if (!activeTask || !flowDoc) {
      return;
    }

    const nextResponse = buildHydratedTaskResponse(activeTask, flowDoc);
    const serializedResponse = JSON.stringify(nextResponse);
    const taskChanged = hydratedTaskIdRef.current !== activeTask.id;
    const remoteChanged = hydratedResponseRef.current !== serializedResponse;

    if (taskChanged || (!localDraftDirtyRef.current && remoteChanged)) {
      setDraftResponse(nextResponse);
      hydratedTaskIdRef.current = activeTask.id;
      setHydratedTaskId(activeTask.id);
      hydratedResponseRef.current = serializedResponse;
      if (taskChanged) {
        localDraftDirtyRef.current = false;
        setAutosaveStatus('');
      }
    }
  }, [activeTask, flowDoc]);

  useEffect(() => {
    if (activeTask?.type === 'Theory Questions') {
      setTheoryPage(0);
      setShowTheoryRetryIntro(false);
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }
  }, [activeTask?.id, activeTask?.type]);

  useEffect(() => {
    if (activeTask?.type === 'Theory Questions' && !showTheoryRetryIntro) {
      window.requestAnimationFrame(() => window.scrollTo({ top: 0, left: 0, behavior: 'auto' }));
    }
  }, [activeTask?.type, showTheoryRetryIntro, theoryPage]);

  const handleTheoryAnswerChange = (questionId, value) => {
    if (['submitted', 'completed'].includes(activeTaskState?.status)) return;
    setDraftResponse((current) => {
      return {
        ...current,
        answers: {
          ...(current.answers || {}),
          [questionId]: value,
        },
      };
    });
    localDraftDirtyRef.current = true;
    setAutosaveStatus('');
  };

  const saveTheoryDraft = async () => {
    if (!flowDoc?.id || activeTask?.type !== 'Theory Questions' || ['submitted', 'completed'].includes(activeTaskState?.status)) {
      return true;
    }

    setSavingTheoryPage(true);
    setAutosaveStatus('Saving...');
    try {
      await updateDoc(
        doc(db, 'trafficCourseFlows', flowDoc.id),
        new FieldPath('responses', String(activeTask.id)),
        draftResponse,
        'updatedAt',
        new Date().toISOString(),
      );
      hydratedTaskIdRef.current = activeTask.id;
      hydratedResponseRef.current = JSON.stringify(draftResponse);
      localDraftDirtyRef.current = false;
      setAutosaveStatus('Answers saved');
      return true;
    } catch (saveError) {
      setAutosaveStatus('Could not save answers');
      console.error('Unable to save theory answers', saveError);
      return false;
    } finally {
      setSavingTheoryPage(false);
    }
  };

  useEffect(() => {
    if (activeTask?.type !== STUDENT_DECLARATION_TYPE || !draftResponse?.signature || !signatureRef.current) {
      return;
    }

    try {
      signatureRef.current.fromDataURL(draftResponse.signature);
    } catch (restoreError) {
      console.error('Unable to restore traffic course signature', restoreError);
    }
  }, [activeTask, draftResponse]);

  const handleSubmitTask = async () => {
    if (!flowDoc || !activeTask || !isTrafficTaskAccessible(flowDoc, activeTask)
      || ['submitted', 'completed'].includes(activeTaskState?.status)) return;

    setAutosaveStatus('');

    const signatureReady = activeTask.type === STUDENT_DECLARATION_TYPE
      ? (!signatureRef.current?.isEmpty() && signatureRef.current?.getTrimmedCanvas().toDataURL('image/png'))
      : '';

    if (!isTaskResponseComplete(activeTask, draftResponse, signatureReady)) {
      setError('Please complete all required responses in this section.');
      return;
    }

    setSaving(true);
    showLoadingMessage('Submitting');
    setError('');

    let responsePayload = activeTask.type === STUDENT_DECLARATION_TYPE
      ? {
        ...draftResponse,
        signature: signatureReady,
      }
      : draftResponse;
    let nextTaskStates = buildNextTrafficTaskStates(flowDoc, activeTask.id);
    // A signed student declaration is final student evidence; it does not need
    // a second trainer completion action before the assessor declaration opens.
    // A fully auto-marked theory section is the same: submission is only reached
    // once every automatic answer is correct, so there is nothing left to assess.
    const completesOnSubmission = activeTask.type === STUDENT_DECLARATION_TYPE
      || (activeTask.type === 'Theory Questions' && !theoryNeedsTrainerMarking(activeTask));
    if (completesOnSubmission && nextTaskStates[activeTask.id]) {
      nextTaskStates = {
        ...nextTaskStates,
        [activeTask.id]: {
          ...nextTaskStates[activeTask.id],
          status: 'completed',
          completedAt: new Date().toISOString(),
        },
      };
    }
    const activeTaskIndex = visibleTasks.findIndex((task) => task.id === activeTask.id);
    const canProceed = task => nextTaskStates[task.id]?.status === 'available'
      && isTrafficTaskAccessible({ ...flowDoc, taskStates: nextTaskStates }, task);
    const nextAvailableTask = visibleTasks.slice(activeTaskIndex + 1).find(canProceed)
      || visibleTasks.find(canProceed);

    const handleSubmissionFailure = (submitError) => {
      clearPageMessage();
      setSaving(false);
      setCompletionMessage('');
      setError('This section could not be submitted. Please try again.');
      showWarningMessage('This section could not be submitted. Please try again.');
      console.error('Traffic course flow submission failed', submitError);
    };

    const showSubmittedState = (nextResponse, nextStates) => {
      const submittedFlowDoc = {
        ...flowDoc,
        responses: {
          ...(flowDoc.responses || {}),
          [activeTask.id]: nextResponse,
        },
        taskStates: nextStates,
        updatedAt: new Date().toISOString(),
      };
      setFlowDoc(submittedFlowDoc);
      hydratedTaskIdRef.current = activeTask.id;
      hydratedResponseRef.current = JSON.stringify(nextResponse);
      localDraftDirtyRef.current = false;
      setSaving(false);
      setAutosaveStatus('');
      clearPageMessage();
      setCompletionMessage('Completed');
      window.setTimeout(() => {
        setCompletionMessage('');
        if (activeTask.type !== 'Theory Questions' && nextAvailableTask?.id) {
          setActiveTaskId(nextAvailableTask.id);
        }
      }, 2200);
    };

    try {
      if (activeTask.type === 'Theory Questions') {
        const autoMark = markTheoryResponse(activeTask, responsePayload);
        responsePayload = {
          ...responsePayload,
          autoMark: {
            ...autoMark,
            attempts: Number(responsePayload.autoMark?.attempts || 0) + 1,
            markedAt: new Date().toISOString(),
          },
        };

        if (autoMark.incorrectQuestionIds.length > 0) {
          const retryAnswers = { ...(responsePayload.answers || {}) };
          autoMark.incorrectQuestionIds.forEach((questionId) => delete retryAnswers[questionId]);
          const retryResponse = { ...responsePayload, answers: retryAnswers };
          await updateDoc(
            doc(db, 'trafficCourseFlows', flowDoc.id),
            new FieldPath('responses', String(activeTask.id)),
            retryResponse,
            'updatedAt',
            new Date().toISOString(),
          );

          // Only present a retry after the response and its marks are durable.
          hydratedTaskIdRef.current = activeTask.id;
          hydratedResponseRef.current = JSON.stringify(retryResponse);
          localDraftDirtyRef.current = false;
          setFlowDoc((current) => current ? {
            ...current,
            responses: {
              ...(current.responses || {}),
              [activeTask.id]: retryResponse,
            },
          } : current);
          setDraftResponse(retryResponse);
          setTheoryPage(0);
          setShowTheoryRetryIntro(true);
          setSaving(false);
          clearPageMessage();
          setError(`${autoMark.incorrectQuestionIds.length} question${autoMark.incorrectQuestionIds.length === 1 ? '' : 's'} need another attempt.`);
          window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
          return;
        }

      }

      if (activeTaskState?.returnedForCorrection) responsePayload = clearReturnedTrafficMarks(responsePayload);
      await updateDoc(
        doc(db, 'trafficCourseFlows', flowDoc.id),
        new FieldPath('responses', String(activeTask.id)),
        responsePayload,
        'taskStates',
        nextTaskStates,
        'updatedAt',
        new Date().toISOString(),
      );
      showSubmittedState(responsePayload, nextTaskStates);
    } catch (submitError) {
      handleSubmissionFailure(submitError);
    }
  };

  const allStudentTasksSubmitted = visibleTasks.length > 0 && visibleTasks.every((task) => isTaskSubmitted(flowDoc, task.id));
  const declarationPath = getTrafficDeclarationPath(flowDoc || {});

  useEffect(() => {
    if (flowDoc?.classStatus !== 'cancelled' && declarationPath && allStudentTasksSubmitted && !saving && !completionMessage) {
      navigate(declarationPath, { replace: true });
    }
  }, [allStudentTasksSubmitted, completionMessage, declarationPath, navigate, saving, flowDoc?.classStatus]);

  if (loading) {
    return <div style={{ padding: '60px 20px', textAlign: 'center' }}>Loading traffic course...</div>;
  }

  if (error && !flowDoc) {
    return <div style={{ padding: '60px 20px', textAlign: 'center' }}><p role='alert'>{error}</p><button type='button' onClick={() => setRefreshAttempt(value => value + 1)}>Try again</button></div>;
  }

  if (flowDoc?.classStatus === 'cancelled') return <main style={{ padding: '32px' }}>
    <h1>Class cancelled</h1><p>Your saved answers have been retained. Please contact your trainer for the updated arrangements.</p>
  </main>;

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', padding: '24px 18px 48px' }}>
      {completionMessage && (
        <div
          role='status'
          aria-live='polite'
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(15, 23, 42, 0.22)',
            padding: '20px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', padding: '22px 32px', border: '1px solid #a7d8b6', borderRadius: '16px', background: '#edf8f0', color: '#287a45', fontSize: '22px', fontWeight: 700, boxShadow: '0 18px 45px rgba(15, 23, 42, 0.18)', textAlign: 'center' }}>
            <svg aria-hidden='true' width='30' height='30' viewBox='0 0 24 24' fill='#dcfce7' stroke='currentColor' strokeWidth='2.25' strokeLinecap='round' strokeLinejoin='round'>
              <circle cx='12' cy='12' r='9' />
              <path d='m8 12 2.7 2.7L16.5 9' />
            </svg>
            <span>{completionMessage}</span>
          </div>
        </div>
      )}
      <div style={{ maxWidth: '1120px', margin: '0 auto' }}>
        <div style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '18px' }}>
            <img src={Logo} alt='Upskill Institute' style={{ display: 'block', width: '130px', maxWidth: '100%' }} />
          </div>
          <div>
            <h1 style={{ margin: '0 0 8px', fontSize: '34px' }}>{flowDoc?.courseName || 'Traffic Course'}</h1>
            <p style={{ margin: 0, color: '#475569' }}><strong>Student Name:</strong> {flowDoc?.firstName} {flowDoc?.lastName}</p>
            {flowDoc?.priorLearning && <p role='status' style={{ color: '#287a45' }}>
              Completed shared sections have been credited from your Traffic Control course on {flowDoc.priorLearning.sourceCourseDate}.
              {flowDoc.eoiReuse ? ' Your verified EOI has also been reused.' : ' Your EOI is recorded for this course.'}
              {' '}Please complete the remaining sections and a new student declaration. Your trainer will sign this course separately.
            </p>}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '280px minmax(0, 1fr)', gap: '20px', alignItems: 'start' }}>
          <aside style={stageCardStyle}>
            <strong style={{ display: 'block', marginBottom: '14px' }}>Sections</strong>
            <div style={{ display: 'grid', gap: '10px' }}>
              {visibleTasks.map((task) => {
                const state = getTaskState(flowDoc, task.id);
                const isActive = activeTask?.id === task.id;
                return (
                  <button
                    key={task.id}
                    type='button'
                    onClick={() => setActiveTaskId(task.id)}
                    disabled={!isTrafficTaskAccessible(flowDoc, task)}
                    style={{
                      textAlign: 'left',
                      padding: '12px 14px',
                      borderRadius: '12px',
                      border: isActive ? '2px solid #111827' : '1px solid #dbe2ea',
                      background: isActive ? '#fff7ed' : '#fff',
                      opacity: state?.status === 'locked' ? 0.55 : 1,
                      cursor: state?.status === 'locked' ? 'not-allowed' : 'pointer',
                    }}
                  >
                    <div style={{ fontWeight: 700, color: '#111827', fontSize: '14px', marginBottom: '4px' }}>{task.name || task.type}</div>
                    <div style={{ fontSize: '12px', color: '#475569' }}>{state?.status === 'completed' && <span aria-hidden='true'>&#10003; </span>}{getTrafficStageStatusLabel(state)}</div>
                  </button>
                );
              })}
            </div>
          </aside>

          <section style={stageCardStyle}>
            {activeTask?.type === 'Theory Questions' && ['submitted', 'completed'].includes(activeTaskState?.status) ? (
              <div role='status'><h2>Completed</h2><p>Please wait for instructions from your Trainer.</p></div>
            ) : activeTask && hydratedTaskId !== activeTask.id ? (
              <div>Loading section...</div>
            ) : activeTask ? (
              <>
                <div style={{ marginBottom: '18px' }}>
                  <h2 style={{ margin: 0, fontSize: '28px' }}>{activeTask.name || activeTask.type}</h2>
                </div>

                {activeTaskState?.returnedForCorrection && <div style={{ ...contentBlockStyle, borderColor: '#f3b35b', color: '#92400e' }}>
                  Your trainer has returned this section for correction. Review the items marked NYC and submit again.
                  {activeTask.type === 'Activity' && <ul>{(activeTask.assessorChecklist?.length ? activeTask.assessorChecklist : [{ id: 'overall-activity-outcome', text: 'Overall activity outcome' }])
                    .filter(entry => draftResponse.assessorReview?.checklistMarks?.[entry.id] === 'NYC')
                    .map(entry => <li key={entry.id}>{entry.text}</li>)}</ul>}
                </div>}
                {activeTask.type === 'Student instructions' && (
                  <div style={contentBlockStyle}>
                    <div dangerouslySetInnerHTML={{ __html: activeTask.instructions || '<p>No instructions added.</p>' }} />
                  </div>
                )}

                {activeTask.type === 'Conditions of assessment' && (
                  <div style={contentBlockStyle}>
                    <div dangerouslySetInnerHTML={{ __html: activeTask.conditions || '<p>No conditions added.</p>' }} />
                  </div>
                )}

                {activeTask.type === 'Theory Questions' && (
                  <div>
                    {showTheoryRetryIntro ? (
                      <div style={{ minHeight: '360px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '28px' }}>
                        <div style={{ width: '100%', maxWidth: '520px', padding: '34px 30px', border: '1px solid #f3b35b', borderRadius: '18px', background: '#fffaf0', boxShadow: '0 14px 34px rgba(15, 23, 42, 0.08)', textAlign: 'center' }}>
                          <h3 style={{ margin: '0 0 14px', color: '#92400e', fontSize: '25px' }}>Not all questions are correct.</h3>
                          <p style={{ margin: '0 0 8px', color: '#334155', fontSize: '20px', fontWeight: 700 }}>Have another go!</p>
                          <p style={{ margin: '0 0 26px', color: '#334155', fontSize: '20px', fontWeight: 700 }}>Good luck.</p>
                          <button
                            type='button'
                            onClick={() => {
                              setShowTheoryRetryIntro(false);
                              setError('');
                              window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
                            }}
                            style={{ minWidth: '140px', minHeight: '44px', padding: '10px 18px', border: '1px solid #d6bd19', borderRadius: '11px', background: '#facc15', color: '#111827', fontSize: '15px', fontWeight: 700, cursor: 'pointer' }}
                          >
                            Next →
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                    {remainingRetryTheoryQuestionIds.length > 0 && (
                      <div style={{ marginBottom: '16px', padding: '12px 14px', border: '1px solid #f3b35b', borderRadius: '12px', background: '#fff7db', color: '#92400e', fontWeight: 700 }}>
                        Please retry the {remainingRetryTheoryQuestionIds.length} question{remainingRetryTheoryQuestionIds.length === 1 ? '' : 's'} marked incorrect.
                      </div>
                    )}
                    {(theoryQuestionPages[theoryPage] || []).map((question) => (
                      <QuestionRenderer
                        key={question.id}
                        question={question}
                        value={draftResponse.answers?.[question.id]}
                        onChange={(value) => handleTheoryAnswerChange(question.id, value)}
                      />
                    ))}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '14px', marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
                      <button
                        type='button'
                        disabled={savingTheoryPage || theoryPage === 0}
                        onClick={async () => {
                          if (!await saveTheoryDraft()) return;
                          setTheoryPage((current) => Math.max(0, current - 1));
                          window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
                        }}
                        style={{ minWidth: '130px', minHeight: '42px', padding: '10px 16px', border: '1px solid #cbd5e1', borderRadius: '11px', background: savingTheoryPage || theoryPage === 0 ? '#f1f5f9' : '#fff', color: savingTheoryPage || theoryPage === 0 ? '#94a3b8' : '#334155', fontWeight: 700, cursor: savingTheoryPage || theoryPage === 0 ? 'not-allowed' : 'pointer' }}
                      >
                        ← Previous Page
                      </button>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ color: '#475569', fontSize: '13px', fontWeight: 600 }}>Page {theoryPage + 1} of {Math.max(theoryQuestionPages.length, 1)}</div>
                        {autosaveStatus && <div style={{ marginTop: '4px', color: autosaveStatus === 'Could not save answers' ? '#b91c1c' : autosaveStatus === 'Answers saved' ? '#287a45' : '#64748b', fontSize: '12px' }}>{autosaveStatus}</div>}
                      </div>
                      <button
                        type='button'
                        disabled={savingTheoryPage || !currentTheoryPageComplete || theoryPage >= theoryQuestionPages.length - 1}
                        onClick={async () => {
                          if (!await saveTheoryDraft()) return;
                          setTheoryPage((current) => Math.min(theoryQuestionPages.length - 1, current + 1));
                          window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
                        }}
                        title={!currentTheoryPageComplete ? 'Answer every question on this page to continue.' : ''}
                        style={{ minWidth: '130px', minHeight: '42px', padding: '10px 16px', border: '1px solid #d6bd19', borderRadius: '11px', background: savingTheoryPage || !currentTheoryPageComplete || theoryPage >= theoryQuestionPages.length - 1 ? '#f1f5f9' : '#facc15', color: savingTheoryPage || !currentTheoryPageComplete || theoryPage >= theoryQuestionPages.length - 1 ? '#94a3b8' : '#111827', fontWeight: 700, cursor: savingTheoryPage || !currentTheoryPageComplete || theoryPage >= theoryQuestionPages.length - 1 ? 'not-allowed' : 'pointer' }}
                      >
                        Next Page →
                      </button>
                    </div>
                      </>
                    )}
                  </div>
                )}

                {activeTask.type === 'Activity' && (
                  <>
                    {activeTask.instructions && <div style={contentBlockStyle}><strong style={{ display: 'block', marginBottom: '10px' }}>Instructions</strong><div dangerouslySetInnerHTML={{ __html: activeTask.instructions }} /></div>}
                    {activeTask.activityScenario && <div style={contentBlockStyle}><strong style={{ display: 'block', marginBottom: '10px' }}>Scenario</strong><div dangerouslySetInnerHTML={{ __html: activeTask.activityScenario }} /></div>}
                    {(activeTask.assessorChecklist || []).length > 0 && (
                      <div style={contentBlockStyle}>
                        <strong style={{ display: 'block', marginBottom: '10px' }}>Activity requirements</strong>
                        <p style={{ marginTop: 0, color: '#64748b' }}>Your trainer / assessor reviews this activity after you submit it.</p>
                        <div style={{ display: 'grid', gap: '8px' }}>
                          {(activeTask.assessorChecklist || []).map((entry) => (
                            <div key={entry.id} style={{ border: '1px solid #e5e7eb', borderRadius: '10px', padding: '10px 12px', background: '#f8fafc' }}>
                              <strong style={{ display: 'block', marginBottom: '4px' }}>{entry.numberLabel || ''} {entry.text}</strong>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}

                {activeTask.type === STUDENT_DECLARATION_TYPE && (
                  <>
                    <div style={contentBlockStyle}>
                      <div dangerouslySetInnerHTML={{ __html: activeTask.declarationContent || '<p>No declaration content added.</p>' }} />
                    </div>
                    <div style={{ ...contentBlockStyle, display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '14px' }}>
                      <label>
                        <span style={{ display: 'block', marginBottom: '6px', fontWeight: 700 }}>{activeTask.declarationNameLabel || 'Student Name'}</span>
                        <input
                          value={draftResponse.studentName || ''}
                          readOnly
                          aria-readonly='true'
                          style={{ width: '100%', border: '1px solid #cbd5e1', borderRadius: '10px', padding: '12px', boxSizing: 'border-box', background: '#e5e7eb', color: '#64748b' }}
                        />
                      </label>
                      <label>
                        <span style={{ display: 'block', marginBottom: '6px', fontWeight: 700 }}>{activeTask.declarationDateLabel || 'Date'}</span>
                        <input
                          type='date'
                          value={draftResponse.declarationDate || ''}
                          disabled
                          readOnly
                          style={{ width: '100%', border: '1px solid #cbd5e1', borderRadius: '10px', padding: '12px', boxSizing: 'border-box', background: '#e5e7eb', color: '#64748b', cursor: 'not-allowed' }}
                        />
                      </label>
                      <div style={{ gridColumn: '1 / -1' }}>
                        <span style={{ display: 'block', marginBottom: '8px', fontWeight: 700 }}>{activeTask.declarationSignatureLabel || 'Student Signature'}</span>
                        <SignatureCanvas
                          penColor='black'
                          ref={signatureRef}
                          canvasProps={{ width: 720, height: 220, className: 'sigCanvas' }}
                        />
                        <div style={{ marginTop: '10px' }}>
                          <button
                            type='button'
                            onClick={() => signatureRef.current?.clear()}
                            style={{
                              minHeight: '40px',
                              padding: '9px 14px',
                              border: '1px solid #fecaca',
                              borderRadius: '10px',
                              background: '#fef2f2',
                              color: '#b91c1c',
                              fontFamily: "'Rubik', sans-serif",
                              fontSize: '14px',
                              fontWeight: 700,
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '7px',
                              cursor: 'pointer',
                            }}
                          >
                            <svg aria-hidden='true' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
                              <path d='M3 6h18' />
                              <path d='M8 6V4h8v2' />
                              <path d='M19 6l-1 14H6L5 6' />
                            </svg>
                            Clear Signature
                          </button>
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {error && !showTheoryRetryIntro && <div style={{ marginBottom: '14px', color: '#b91c1c', fontWeight: 600 }}>{error}</div>}

                {!['submitted', 'completed'].includes(activeTaskState?.status) && !showTheoryRetryIntro && (activeTask.type !== 'Theory Questions' || theoryPage >= theoryQuestionPages.length - 1) && (
                  <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginTop: '18px' }}>
                    <button
                      type='button'
                      onClick={handleSubmitTask}
                      disabled={saving || activeTaskState?.status === 'locked'}
                      style={{
                        minWidth: '156px',
                        minHeight: '46px',
                        padding: '11px 20px',
                        border: '1px solid #d6bd19',
                        borderRadius: '12px',
                        background: saving || activeTaskState?.status === 'locked' ? '#e5e7eb' : '#facc15',
                        color: saving || activeTaskState?.status === 'locked' ? '#94a3b8' : '#111827',
                        fontFamily: "'Rubik', sans-serif",
                        fontSize: '15px',
                        fontWeight: 700,
                        lineHeight: 1,
                        boxShadow: saving || activeTaskState?.status === 'locked' ? 'none' : '0 4px 12px rgba(202, 138, 4, 0.2)',
                        cursor: saving || activeTaskState?.status === 'locked' ? 'not-allowed' : 'pointer',
                      }}
                    >
                      <span>{saving ? 'Submitting...' : 'Next Section'}</span>
                      {!saving && (
                        <svg aria-hidden='true' width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.25' strokeLinecap='round' strokeLinejoin='round' style={{ marginLeft: '8px', verticalAlign: 'middle' }}>
                          <path d='M5 12h14' />
                          <path d='m13 6 6 6-6 6' />
                        </svg>
                      )}
                    </button>
                  </div>
                )}

                {activeTaskState?.status === 'submitted' && (
                  <div style={{ marginTop: '18px', padding: '12px 14px', borderRadius: '12px', background: '#edf8f0', color: '#287a45', fontWeight: 700 }}>
                    This section has been submitted.
                  </div>
                )}

              </>
            ) : (
              <div>Please wait for instructions from your Trainer.</div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default TrafficCourseFlow;
